import { useState, useEffect, useRef, useCallback } from "react";
import { useJanusStreaming } from "../../lib/janus-react-streaming";
import { useAuth } from "../../contexts/authContext";

const VM_NAME = "sl-spice-test";
const VM_API = `/api/vms/${VM_NAME}`;
const VM_W = 1280, VM_H = 720;
const KEYMAP = {Escape:1,Enter:28,Space:57,Backspace:14,Tab:15,KeyQ:16,KeyW:17,KeyE:18,KeyR:19,KeyT:20,KeyY:21,KeyU:22,KeyI:23,KeyO:24,KeyP:25,KeyA:30,KeyS:31,KeyD:32,KeyF:33,KeyG:34,KeyH:35,KeyJ:36,KeyK:37,KeyL:38,KeyZ:44,KeyX:45,KeyC:46,KeyV:47,KeyB:48,KeyN:49,KeyM:50,ArrowUp:103,ArrowLeft:105,ArrowRight:106,ArrowDown:108};

export default function ScreenPage() {
  const { userData } = useAuth();
  const token = userData?.accessToken;

  const config = {
    server: "/proxy",
    headers: () => (token ? { Token: token } : {}),
    iceServers: [{ urls: "stun:23.137.200.99:3478" }],
    debug: false,
  };

  const { connectionState, iceState, mediaStream, dataChannel, sendData, connect, watch, error: streamError, clearError } = useJanusStreaming(config);
  const [vmState, setVmState] = useState("checking...");
  const [apiError, setApiError] = useState(null);
  const [busy, setBusy] = useState(false);
  const videoRef = useRef(null);
  const connectingRef = useRef(false);

  useEffect(() => {
    const poll = async () => {
      try {
        const r = await fetch(VM_API);
        setVmState(r.status === 404 ? "not found" : (await r.json()).state);
      } catch { setVmState("error"); }
    };
    poll();
    const iv = setInterval(poll, 10000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (connectionState === "disconnected" && !connectingRef.current && token) {
      connectingRef.current = true;
      connect()
        .then(() => watch({ id: 10 }))
        .catch(() => {})
        .finally(() => { connectingRef.current = false; });
    }
  }, [connectionState, connect, watch, token]);

  const send = useCallback((data) => {
    sendData(JSON.stringify(data));
  }, [sendData]);

  const handleMouseMove = (e) => {
    const v = videoRef.current;
    if (!v || document.activeElement !== v) return;
    const r = v.getBoundingClientRect();
    send({ type: "mouse_move", x: Math.round((e.clientX - r.left) / r.width * VM_W), y: Math.round((e.clientY - r.top) / r.height * VM_H) });
  };
  const handleMouseDown = (e) => { e.preventDefault(); videoRef.current?.focus(); send({ type: "mouse_down", button: e.button === 0 ? 1 : e.button === 2 ? 3 : 2 }); };
  const handleMouseUp = (e) => { e.preventDefault(); send({ type: "mouse_up", button: e.button === 0 ? 1 : e.button === 2 ? 3 : 2 }); };
  const handleKeyDown = (e) => { e.preventDefault(); if (KEYMAP[e.code]) send({ type: "key_down", scancode: KEYMAP[e.code] }); };
  const handleKeyUp = (e) => { e.preventDefault(); if (KEYMAP[e.code]) send({ type: "key_up", scancode: KEYMAP[e.code] }); };

  async function vmAction(action) {
    setBusy(true); setApiError(null);
    try {
      let r;
      if (action === "start") r = await fetch(`${VM_API}/start`, { method: "POST" });
      else if (action === "stop") r = await fetch(`${VM_API}/stop?force=true`, { method: "POST" });
      else if (action === "create") {
        r = await fetch("/api/vms", { method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: VM_NAME, vcpus: 1, memory_mb: 256, disk_gb: 10,
            graphics_type: "spice", spice_port: 5950, spice_listen: "0.0.0.0",
            iso: "/var/lib/libvirt/images/slitaz.iso" }) });
      } else if (action === "delete") {
        if (!window.confirm("Delete VM? This removes the disk.")) { setBusy(false); return; }
        r = await fetch(VM_API, { method: "DELETE" });
      }
      if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error(b.error || `HTTP ${r.status}`); }
      const poll = await fetch(VM_API);
      setVmState(poll.status === 404 ? "not found" : (await poll.json()).state);
    } catch (err) { setApiError(err.message); }
    finally { setBusy(false); }
  }

  if (!token) return <div style={{ padding: 24 }}>Please log in to view the VM console.</div>;

  const isRunning = vmState === "running";
  const exists = vmState !== "not found";
  const error = apiError || streamError;

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ marginBottom: 16 }}>VM Console</h2>

      <div style={{ marginBottom: 16, display: "flex", gap: 8, alignItems: "center" }}>
        <span>Status: <strong>{connectionState}</strong></span>
        {iceState !== "new" && <span> | ICE: <strong>{iceState}</strong></span>}
        {dataChannel && <span> | Data channel: <strong>connected</strong></span>}
        <span> | VM: <strong>{vmState}</strong></span>
      </div>

      {error && (
        <div style={{ padding: 12, background: "#fee", color: "#c00", borderRadius: 8, marginBottom: 16 }}>
          {typeof error === "string" ? error : error.message}
          <button onClick={() => { setApiError(null); clearError(); }} style={{ marginLeft: 8 }}>Dismiss</button>
        </div>
      )}

      <div style={{ marginBottom: 16, display: "flex", gap: 8 }}>
        {exists && (isRunning
          ? <button onClick={() => vmAction("stop")} disabled={busy} style={{ padding: "6px 16px" }}>Power Off</button>
          : <button onClick={() => vmAction("start")} disabled={busy} style={{ padding: "8px 24px", fontSize: 16 }}>Power On</button>
        )}
        {!exists && <button onClick={() => vmAction("create")} disabled={busy} style={{ padding: "8px 24px", fontSize: 16 }}>Create VM</button>}
        {exists && !isRunning && <button onClick={() => vmAction("delete")} disabled={busy} style={{ padding: "6px 16px", background: "#ef4444", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer" }}>Delete VM</button>}
      </div>

      {mediaStream ? (
        <video
          ref={el => { videoRef.current = el; if (el && el.srcObject !== mediaStream) el.srcObject = mediaStream; }}
          autoPlay playsInline muted tabIndex={0}
          style={{ width: "100%", maxWidth: 1280, borderRadius: 8, background: "#000", cursor: "default", outline: "none" }}
          onMouseMove={handleMouseMove} onMouseDown={handleMouseDown} onMouseUp={handleMouseUp}
          onKeyDown={handleKeyDown} onKeyUp={handleKeyUp} onContextMenu={e => e.preventDefault()}
        />
      ) : (
        <div style={{ width: "100%", maxWidth: 1280, height: 720, borderRadius: 8, background: "#000", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ color: "#888", fontSize: 18 }}>{isRunning ? "Connecting..." : "VM is off"}</span>
        </div>
      )}
    </div>
  );
}
